import {createStyles, Textarea} from '@mantine/core';
import React, {useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';

import {useAnalysisTemplateDescriptionState} from './useAnalysisTemplateDescriptionState';
import {useAnalysisTemplateNameState} from './useAnalysisTemplateNameState';

export interface DuplicateAnalysisTemplateModalProps {
  sourceAnalysisTemplateName: string;
  sourceAnalysisTemplateCollectionId: string;
  sourceTemplateIsPublished: boolean;
  sourceTemplateDescription: string | null;
  actionOrigin?: ActionOrigin;
  onRequestClose: () => void;
  onRequestSubmit: (params: {
    name: string;
    templateCollectionId: string;
    description: string | null;
  }) => Promise<void>;
}

const useStyles = createStyles(() => ({
  input: {
    resize: 'vertical',
  },
}));

export default function DuplicateAnalysisTemplateModal(
  props: DuplicateAnalysisTemplateModalProps
): JSX.Element {
  const {name, trimmedName, isNameBlank, nameError, setName} = useAnalysisTemplateNameState(
    `Copy of ${props.sourceAnalysisTemplateName}`
  );
  const [templateCollectionId, setTemplateCollectionId] = useState<string>(
    props.sourceAnalysisTemplateCollectionId
  );
  const {
    description,
    normalizedDescription,
    isDescriptionTooLong,
    descriptionHelpText,
    descriptionError,
    setDescription,
  } = useAnalysisTemplateDescriptionState(props.sourceTemplateDescription);

  const {classes} = useStyles();

  const submitDisabledTooltip = nameError ?? descriptionError;
  const submitDisabled = submitDisabledTooltip != null;

  return (
    <Modal
      title="Duplicate template"
      buttons={[
        {
          text: 'Duplicate',
          submit: true,
          disabled: submitDisabled,
          tooltipTitle: submitDisabledTooltip,
        },
      ]}
      onRequestSubmit={async () => {
        await props.onRequestSubmit({
          name: trimmedName,
          templateCollectionId,
          description: normalizedDescription,
        });
      }}
      onRequestClose={() => {
        props.onRequestClose();
      }}
      analyticsModule={ANALYTICS_MODULE.ANALYSIS_TOOL}
      analyticsModalType="duplicate_analysis_template"
      analyticsMetadata={{
        actionOrigin: props.actionOrigin,
        sourceTemplateStatus: props.sourceTemplateIsPublished ? 'published' : 'draft',
      }}
      dataTestAttrs={{component: 'DuplicateAnalysisTemplateModal', element: 'root'}}
    >
      <FormGroup>
        <Row>
          <Col md={6}>
            <Label>Name</Label>
            <TextInput
              value={name}
              hasError={isNameBlank}
              onChange={(e) => {
                setName(e.target.value);
              }}
              autoFocus={true}
              {...getTestProps({component: 'DuplicateAnalysisTemplateModal', element: 'nameInput'})}
            />
          </Col>
          <Col md={6}>
            <Label>Template collection</Label>
            <TemplateCollectionSelector
              searchable={true}
              value={templateCollectionId}
              onChange={(collection) => {
                setTemplateCollectionId(collection.id);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Textarea
              // Mantine's Textarea sets `resize: none`; the legacy `.modal-body textarea` rule currently
              // overrides it, so we set it explicitly to keep vertical resizing regardless of the modal wrapper.
              classNames={{input: classes.input}}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              label="Description"
              description={descriptionHelpText}
              error={isDescriptionTooLong}
              value={description ?? ''}
              onChange={(e) => {
                setDescription(e.currentTarget.value);
              }}
            />
          </Col>
        </Row>
      </FormGroup>
    </Modal>
  );
}
